"use client";

import type { RouterInput } from "@lattice/backend/trpc";
import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";
import React, { useState } from "react";
import { AreaSeries, ColorType, createChart } from "lightweight-charts";
import { useTheme } from "next-themes";
import { useAtom } from "jotai";
import { interval_atom } from "./chart-atoms";
import { Skeleton } from "../ui/skeleton";
import { useWindowSize } from "@reactuses/core";
import IntervalToggle from "./interval-toggle";
import { toChartTime, fromChartTime } from "./utils";

export type T_Interval =
  RouterInput["stock"]["get_historical_price"]["interval"];

function isSinglePriceOhlc(ohlc: {
  o: number;
  h: number;
  l: number;
  c: number;
}) {
  return ohlc.o === ohlc.h && ohlc.h === ohlc.l && ohlc.l === ohlc.c;
}

export default function NonDraggableStockPriceChart({
  symbol,
  is_crypto,
}: {
  symbol: string;
  is_crypto: boolean;
}) {
  const [interval, setInterval] = useAtom(interval_atom);
  const [chart, setChart] = useState<ReturnType<typeof createChart>>();
  const chart_container_ref = React.useRef<HTMLDivElement>(null);
  const { width: window_width } = useWindowSize();

  const {
    data: all_interval_bars,
    isPending,
    isError,
  } = trpcApi.stock.get_historical_price_all.useQuery({
    symbol,
    is_crypto,
  });

  const bars = all_interval_bars?.[interval];

  const { resolvedTheme } = useTheme();

  // --- Memoize lookup map whenever bars change
  type T_OHLC = { o: number; h: number; l: number; c: number };
  const ohlcMapRef = React.useRef<Map<number, T_OHLC>>(new Map());

  React.useMemo(() => {
    if (!bars) return;
    const map = new Map<number, T_OHLC>();
    for (const b of bars) {
      const ts = Math.floor(new Date(b.t).getTime() / 1000);
      map.set(ts, { o: b.o, h: b.h, l: b.l, c: b.c });
    }
    ohlcMapRef.current = map;
  }, [bars]);

  React.useEffect(() => {
    if (!chart_container_ref.current || !bars) return;

    const chart = createChart(chart_container_ref.current, {
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: resolvedTheme === "dark" ? "#a1a1aa" : "#71717a",
      },
      width: chart_container_ref.current?.clientWidth,
      height: Math.min((3 * chart_container_ref.current.clientWidth) / 4, 400),
      handleScroll: false,
      handleScale: false,
      timeScale: {
        timeVisible: false,
        visible: false,
        borderColor: "transparent",
      },
      crosshair: {
        horzLine: {
          color:
            resolvedTheme === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.1)",
          labelBackgroundColor:
            resolvedTheme === "dark" ? "#27272a" : "#f4f4f5",
        },
        vertLine: {
          color:
            resolvedTheme === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.1)",
          labelBackgroundColor:
            resolvedTheme === "dark" ? "#27272a" : "#f4f4f5",
        },
      },
      grid: {
        vertLines: {
          visible: false,
        },
        horzLines: {
          color:
            resolvedTheme === "dark"
              ? "rgba(255, 255, 255, 0.03)"
              : "rgba(0, 0, 0, 0.03)",
        },
      },
    });

    const tooltip = document.createElement("div");
    tooltip.className = `
      absolute z-50 pointer-events-none 
      bg-background border border-border 
      text-card-foreground text-sm 
      px-3 py-2 rounded-lg shadow-lg
      backdrop-blur-sm
      `;
    tooltip.style.display = "none";
    chart_container_ref.current.appendChild(tooltip);

    const isUp = (bars.at(-1)?.c ?? 0) > (bars.at(0)?.c ?? 0);
    const newSeries = chart.addSeries(AreaSeries, {
      lineColor: isUp ? "rgb(0, 200, 5)" : "rgb(255, 80, 0)",
      topColor: isUp ? "rgba(0, 200, 5, 0.15)" : "rgba(255, 80, 0, 0.15)",
      bottomColor: isUp ? "rgba(0, 200, 5, 0)" : "rgba(255, 80, 0, 0)",
      lineWidth: 2,
    });

    newSeries.setData(
      bars.map((b) => ({
        time: toChartTime(new Date(b.t).getTime()),
        value: b.c,
      })),
    );

    chart.subscribeCrosshairMove((param) => {
      if (!param.time || !param.point) {
        tooltip.style.display = "none";
        return;
      }

      const ts = fromChartTime(param.time);
      const ohlc = ohlcMapRef.current.get(ts);

      if (!ohlc) {
        tooltip.style.display = "none";
        return;
      }

      // Format timestamp for display
      const date = new Date(ts * 1000);
      const timeString = date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      const priceRows = isSinglePriceOhlc(ohlc)
        ? `
          <div class="flex justify-between gap-4">
            <span class="text-muted-foreground">Price:</span>
            <span class="font-mono font-medium">${ohlc.c.toFixed(2)}</span>
          </div>
        `
        : `
          <div class="flex justify-between gap-4">
            <span class="text-muted-foreground">Open:</span>
            <span class="font-mono">${ohlc.o.toFixed(2)}</span>
          </div>
          <div class="flex justify-between gap-4">
            <span class="text-muted-foreground">High:</span>
            <span class="font-mono text-green-600">${ohlc.h.toFixed(2)}</span>
          </div>
          <div class="flex justify-between gap-4">
            <span class="text-muted-foreground">Low:</span>
            <span class="font-mono text-red-600">${ohlc.l.toFixed(2)}</span>
          </div>
          <div class="flex justify-between gap-4">
            <span class="text-muted-foreground">Close:</span>
            <span class="font-mono font-medium">${ohlc.c.toFixed(2)}</span>
          </div>
        `;

      tooltip.innerHTML = `
        <div class="space-y-1">
          <div class="text-center font-medium text-foreground border-b border-border pb-1 mb-2">
            ${timeString}
          </div>
          ${priceRows}
        </div>
      `;
      tooltip.style.display = "block";
      tooltip.style.left = `${param.point.x + 10}px`;
      const yCoordinate = newSeries.priceToCoordinate(ohlc.c);
      tooltip.style.top = `${(yCoordinate ?? 0) - 40}px`;
    });

    const handleResize = () => {
      chart.applyOptions({ width: chart_container_ref.current?.clientWidth });
    };

    window.addEventListener("resize", handleResize);
    chart.timeScale().fitContent();

    setChart(chart);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
      tooltip.remove();
    };
  }, [resolvedTheme, bars]);

  // this is required so that the data on the left doesnt get cut off when resizing window
  React.useEffect(() => {
    chart?.timeScale().fitContent();
    chart?.applyOptions({
      height: Math.min(
        (3 * (chart_container_ref.current?.clientWidth ?? 0)) / 4,
        400,
      ),
    });
  }, [chart, window_width]);

  if (isError)
    return (
      <div className="w-full h-full flex items-center justify-center min-h-62.5 text-muted-foreground">
        No price data available
      </div>
    );

  return (
    <div className="w-full flex flex-col">
      <div
        className="relative min-h-62.5 w-full bg-background"
        ref={chart_container_ref}
      >
        {isPending && (
          <div className="absolute inset-0 z-10 bg-background/80 backdrop-blur-sm flex items-center justify-center">
            <div className="space-y-3">
              <Skeleton className="h-2 w-32 mx-auto" />
              <Skeleton className="h-1 w-24 mx-auto" />
            </div>
          </div>
        )}
        {!isPending && bars && bars.length === 0 && (
          <div className="flex items-center justify-center text-muted-foreground h-full w-full absolute top-0 left-0">
            No data available
          </div>
        )}
      </div>
      <IntervalToggle interval={interval} setInterval={setInterval} />
    </div>
  );
}
